import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './OTPEntry.scss';
import { otpService } from 'api/otp.servic';

type Stage = 'email-verification' | 'otp-entry';

const OTPEntry: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [stage, setStage] = useState<Stage>('email-verification');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    // If a valid OTP session already exists for this application, skip re-authentication
    // and go straight to the application. Avoids a needless OTP round-trip and prevents
    // revoking a still-valid session (a fresh validation revokes the previous one).
    useEffect(() => {
        if (id && otpService.getSessionToken(id)) {
            navigate(`/new-application/${id}`, { replace: true });
        }
    }, [id, navigate]);

    const handleVerifyEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            console.log('Verifying email for application ID:', id, 'with email:', email);
            const response = await otpService.verifyEmail(id, email);
            console.log('Email verification successful:', response);

            // Email verified successfully - move to OTP stage
            setStage('otp-entry');
            // Automatically request OTP after email verification
            await handleRequestOTP();
        } catch (err: any) {
            console.error('Email verification error:', err);
            if (err.response?.status === 404 || err.message?.includes('Application not found')) {
                // Application not found - redirect to not found page
                navigate('/not-found');
                return;
            } else if (err.response?.status === 403 || err.message?.includes('does not match')) {
                setError('כתובת המייל אינה תואמת למייל המשויך להגשה זו.');
            } else {
                setError('אימות המייל נכשל. אנא נסה שוב.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRequestOTP = async () => {
        setLoading(true);
        setError('');
        try {
            console.log('Making OTP request for application ID:', id);
            const response = await otpService.request(id);
            console.log('OTP request successful:', response.data);
        } catch (err: any) {
            console.error('OTP request error:', err);
            if (err.response?.status === 404 || err.message?.includes('Application not found')) {
                // Application not found - redirect to not found page
                navigate('/not-found');
                return;
            }
            if (err.response?.status === 422) {
                // No phone number registered -> cannot deliver the SMS code.
                setError('לא רשום מספר טלפון נייד עבור הגשה זו, ולכן לא ניתן לשלוח קוד אימות ב-SMS. אנא פנה לתמיכה.');
                return;
            }
            setError('שליחת קוד האימות נכשלה. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            console.log('Validating OTP for application ID:', id, 'with code:', code);
            const response = await otpService.validate(id, code);
            console.log('OTP validation successful:', response);

            // Check if we received a session token
            if (response.sessionToken) {
                console.log('✅ Session token received and stored');
                setSuccess(true);

                // Show success message with session info
                setTimeout(() => {
                    navigate(`/new-application/${id}`);
                }, 1500); // Brief delay to show success message
            } else {
                // Fallback for backwards compatibility
                console.log('⚠️ No session token in response, using legacy method');
                localStorage.setItem('otp_validated_' + id, 'true');
                setSuccess(true);
                navigate(`/new-application/${id}`);
            }
        } catch (err: any) {
            console.error('OTP validation error:', err);
            // תרגום הודעות שגיאה נפוצות
            const errorMessage = err.response?.data?.message || err.message || '';
            if (errorMessage.includes('Invalid OTP') || errorMessage.includes('incorrect')) {
                setError('קוד האימות שגוי. אנא נסה שוב.');
            } else if (errorMessage.includes('expired')) {
                setError('קוד האימות פג תוקף. אנא בקש קוד חדש.');
            } else if (errorMessage.includes('attempts')) {
                setError('חרגת ממספר הניסיונות המותר. אנא בקש קוד חדש.');
            } else {
                setError('קוד אימות שגוי. אנא נסה שוב.');
            }
        } finally {
            setLoading(false);
        }
    };

    // No automatic OTP request on mount - user must verify email first

    return (
        <div className="otp-page">
            <div className="otp-entry-container">
                {stage === 'email-verification' && (
                    <>
                        <h2>אימות כתובת מייל</h2>
                        <p>אנא הזן את כתובת המייל שלך לאימות.</p>
                        <form onSubmit={handleVerifyEmail}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="your-email@example.com"
                                disabled={loading}
                                autoComplete="email"
                            />
                            <button type="submit" disabled={loading || !email.trim()}>
                                {loading ? 'מאמת...' : 'המשך לקבלת קוד'}
                            </button>
                        </form>
                        {error && <div className="error">{error}</div>}
                    </>
                )}

                {stage === 'otp-entry' && (
                    <>
                        <h2>הזנת קוד אימות</h2>
                        <p>קוד אימות נשלח אליך במסרון (SMS) למספר הנייד הרשום בבקשה.</p>
                        <p>אנא הזן את הקוד שקיבלת.</p>
                        <form onSubmit={handleSubmit}>
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                maxLength={6}
                                pattern="[0-9]{6}"
                                required
                                placeholder="קוד בן 6 ספרות"
                                disabled={loading}
                                autoComplete="off"
                            />
                            <button type="submit" disabled={loading || code.length !== 6}>
                                {loading ? 'מאמת...' : 'אמת קוד'}
                            </button>
                        </form>
                        {error && <div className="error">{error}</div>}
                        {success && <div className="success">הקוד אומת בהצלחה! מעביר אותך...</div>}
                        <button type="button" onClick={handleRequestOTP} disabled={loading}>
                            שלח קוד מחדש
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default OTPEntry;
